import React, { Component, createRef } from 'react';
import { findDOMNode } from 'react-dom';
import ResizeObserver from 'rc-resize-observer';
import classnames from 'classnames';
import { getGlobalStorage } from '@platform/api';
import { getScrollBarSize, calcVariableVirtualInfo, formatTree, getViewportBoundary } from './utils';

import TableHeader from './components/TableHeader';
import TableBody from './components/TableBody';
import TableFooter from './components/TableFooter';

import './index.less';
import throttle from 'lodash/throttle.js';

const scrollBarSize = getScrollBarSize();

const formatColumn = function (tree, callback) {
    return formatTree(tree, callback, {
        idKey: 'key',
        isLeafKey: 'isleaf',
    });
};

class TableNext extends Component {
    static defaultProps = {
        rowHeight: 30,
        maxBodyHeight: 300, // number | 'auto'
        bufferSize: 1,
        columns: [],
        rows: [],
        autoRowHeight: false,
        scrollContainer: () => window,
    }

    tableWrapperRef = createRef();
    tableHeaderWrapperRef = createRef();
    tableBodyWrapperRef = createRef();
    tableFooterWrapperRef = createRef();
    horizontalScrollBarRef = createRef();
    verticalScrollBarRef = createRef();

    horizontalScrollElements = [
        this.tableHeaderWrapperRef,
        this.tableBodyWrapperRef,
        this.tableFooterWrapperRef,
        this.horizontalScrollBarRef,
    ];
    verticalScrollElements = [
        this.tableBodyWrapperRef,
        this.verticalScrollBarRef,
    ];
    virtualInfo = {};

    readyToScrollX = new Set();
    readyToScrollY = new Set();
    scrollDirection = '';
    isScrollPreventDelay = false;

    constructor(props) {
        super(props);
        this.state = {
            bodyWidth: 0,
            bodyHeight: 0,
            scrollLeft: 0,
        };
        //自动化标志位 0关 1开
        this.ncc_automated_open = getGlobalStorage('localStorage', '_ncc_automated_') === '1';
    }

    render() {
        let { scrollLeft, bodyWidth, fixedTop, fixedBottom, fixedDistance } = this.state;
        let { bufferSize, className, autoRowHeight, flex, virtualConfig = {} } = this.props;

        let columnInfo = this.calcColumnInfo();

        // 横向虚拟滚动信息
        let horizontalVirtualInfo = calcVariableVirtualInfo({
            offset: scrollLeft,
            visibleSize: bodyWidth - columnInfo.left.width - columnInfo.right.width,
            bufferSize,
            itemsSize: columnInfo.middle.columnsWidth,
        });

        if (virtualConfig.x === false) {
            horizontalVirtualInfo = {
                ...horizontalVirtualInfo,
                bottomBlank: 0,
                bottomBlankCount: 0,
                endRenderIndex: columnInfo.middle.leafColumns.length - 1,
                endViewIndex: columnInfo.middle.leafColumns.length - 1,
                startRenderIndex: 0,
                startViewIndex: 0,
                topBlank: 0,
                topBlankCount: 0,
            };
        }

        this.tableBodyScrollWidth = columnInfo.left.width + columnInfo.middle.width + columnInfo.right.width;
        // this.hasVerticalScrollBar = bodyHeight < this.tableBodyWrapperRef.current?.scrollHeight;
        this.hasHorizontalScrollBar = bodyWidth < this.tableBodyScrollWidth + (this.hasVerticalScrollBar ? scrollBarSize : 0);

        let renderInfo = {
            bodyWidth,
            tableBodyWidth: bodyWidth,
            horizontalVirtualInfo,
            columnInfo,
            onVirtualInfo: this.handleVirtualInfo,
            hasVerticalScrollBar: this.hasVerticalScrollBar,
            hasHorizontalScrollBar: this.hasHorizontalScrollBar,
            scrollBarSize,
            isFixLeft: scrollLeft > 0,
            isFixRight: scrollLeft < columnInfo.left.width + columnInfo.middle.width + columnInfo.right.width - bodyWidth,
            fixedTop,
            fixedBottom,
            fixedDistance,
        };

        return <div
            ref={this.tableWrapperRef}
            className={classnames(
                "base-table-next",
                className,
                {
                    'flex-table': flex,
                    'flex-container': flex,
                    'auto-row-height': autoRowHeight,
                },
            )}
            onMouseDownCapture={event => {
                // TableBody和tableHeader可框选
                let isHeaderArea = event.currentTarget.querySelector('.table-next-header-wrapper')?.contains(event.target);
                if (
                    (!event.currentTarget.querySelector('.table-next-body-wrapper')?.contains(event.target) &&
                        !isHeaderArea) ||
                    event.target.className.includes('column-resize-handler')
                ) {
                    return;
                }

                this.startX = event.clientX;
                this.startY = event.clientY;

                const handleMouseMove = event => {
                    // event.preventDefault();
                    this.boundaryScroll(event);
                };

                const handleMouseUp = () => {
                    window.removeEventListener('mousemove', handleMouseMove, true);
                    window.removeEventListener('mouseup', handleMouseUp, true);

                    this.readyToBoundaryScroll = false;
                };

                window.addEventListener('mousemove', handleMouseMove, true);
                window.addEventListener('mouseup', handleMouseUp, true);
            }}
        >
            {this.renderTableHeader(renderInfo)}
            {this.renderTableBody(renderInfo)}
            {this.renderTableFooter(renderInfo)}
            {this.renderHorizontalScrollBar(renderInfo)}
        </div>;
    }

    componentDidMount() {
        this.bindEvent();
        this.handleWindowScroll();
    }

    componentDidUpdate() {
        this.handleWindowScroll();
    }

    componentWillUnmount() {
        this.removeEvent();
    }

    /**
     * 移到上/下边缘时，滚动
     * 触发条件：
     *  - 起始位置在视口内
     *  - 鼠标位置在边缘
     *  - 滚动方向和拖动方向一致
     *  - 拖动距离达到一定数值
     */
    boundaryScroll = event => {
        // console.log(this.getBoundary(), event.clientX, event.clientY);
        let { top, bottom, left, right } = this.getBoundary(),
            x = event.clientX, y = event.clientY;

        if (this.startX < left || this.startX > right || this.startY < top || this.startY > bottom) {
            // 起始位置不在视口时，return
            return;
        }

        let handlerX = findDOMNode(this).querySelector('.horizontal-scroll-bar'),
            handlerY = findDOMNode(this).querySelector('.vertical-scroll-bar');

        this.readyToBoundaryScroll = false;

        if (x < left + 40 && this.startX - x > 20) {
            // console.log('左');
            this.readyToBoundaryScroll = true;
            this.scrollAnimation(() => {
                handlerX.scrollLeft -= 1;
            });
        } else if (x > right - 40 && x - this.startX > 20) {
            // console.log('右');
            this.readyToBoundaryScroll = true;
            this.scrollAnimation(() => {
                handlerX.scrollLeft += 1;
            });
        }
        if (y < top + 40 && this.startY - y > 20) {
            // console.log('上');
            this.readyToBoundaryScroll = true;
            this.scrollAnimation(() => {
                handlerY.scrollTop -= 1;
            });
        } else if (y > bottom - 40 && y - this.startY > 20) {
            // console.log('下');
            this.readyToBoundaryScroll = true;
            this.scrollAnimation(() => {
                handlerY.scrollTop += 1;
            });
        }
    }

    scrollAnimation = callback => {
        this.raf = requestAnimationFrame(() => {
            callback();
            this.readyToBoundaryScroll && this.scrollAnimation(callback);
        });
    }

    getBoundary = () => {
        return getViewportBoundary(findDOMNode(this), { fixed: true });
    }

    handleVirtualInfo = virtualInfo => {
        this.virtualInfo = virtualInfo;
    }

    renderTableHeader = renderInfo => {
        return <TableHeader {...this.props} {...renderInfo} ref={this.tableHeaderWrapperRef}></TableHeader>;
    }

    /**
     * 两种布局模式
     */
    renderTableBody = renderInfo => {
        let { flex, bodyStyle = {}, _onScrollBarStatusChange } = this.props,
            { bodyHeight } = this.state;
        /**
         * 1、容器高度决定内部高度
         *  容器flex
         *  纵向滚动条高度与容器高度统一
         * 2、内部高度决定容器高度
         *  内部设最大高度
         *  纵向滚动条高度和TableBody高度保持统一
         *  可伸缩
         */
        return <ResizeObserver
            onResize={({ height }, target) => {
                if (target.clientWidth && height) {
                    this.setState({
                        bodyWidth: target.clientWidth,
                        bodyHeight: height,
                    });
                }
            }}
        >
            <TableBody
                {...this.props}
                {...renderInfo}
                tableRef={this.tableBodyWrapperRef}
                verticalScrollBarRef={this.verticalScrollBarRef}
                bodyStyle={{
                    ...bodyStyle,
                    height: flex ? bodyHeight : (bodyStyle.height ?? 'auto'),
                    maxHeight: flex ? 'none' : bodyStyle.maxHeight,
                    width: this.ncc_automated_open ? '100%' : undefined,
                }}
                onScrollBarStatusChange={status => {
                    this.hasVerticalScrollBar = status;
                    _onScrollBarStatusChange?.(status);
                    this.forceUpdate();
                }}
            ></TableBody>
        </ResizeObserver>;
    }

    renderTableFooter = renderInfo => {
        return <TableFooter {...this.props} {...renderInfo} ref={this.tableFooterWrapperRef}></TableFooter>;
    }

    // 横向滚动条
    renderHorizontalScrollBar = () => {
        let { bodyWidth, fixedBottom, fixedDistance } = this.state,
            verticalScrollBarSize = this.hasVerticalScrollBar ? scrollBarSize : 0,
            horizontalScrollBarSize = this.hasHorizontalScrollBar ? scrollBarSize : 0;

        return <>
            <div
                className={classnames(
                    "horizontal-scroll-bar-wrapper",
                    { "fix-bottom": fixedBottom },
                )}
                style={{
                    display: 'flex',
                    width: bodyWidth,
                    bottom: fixedDistance,
                }}
            >
                <div
                    ref={this.horizontalScrollBarRef}
                    style={{ width: bodyWidth - verticalScrollBarSize, height: horizontalScrollBarSize }}
                    className="horizontal-scroll-bar"
                >
                    <div className="horizontal-scroll-item" style={{ width: this.tableBodyScrollWidth, height: horizontalScrollBarSize, minHeight: 1 }}></div>
                </div>
                {this.hasVerticalScrollBar && <div className="scroll-bar-blank" style={{ height: horizontalScrollBarSize, width: verticalScrollBarSize }}></div>}
            </div>
            {fixedBottom && <div
                style={{
                    width: bodyWidth,
                    height: horizontalScrollBarSize,
                }}
            ></div>}
        </>;
    }

    calcColumnInfo = () => {
        // TODO:可以缓存
        let leftColumns = [], middleColumns = [], rightColumns = [];
        let leftWidth = 0, middleWidth = 0, rightWidth = 0; // 总宽度
        let leftWidths = [], middleWidths = [], rightWidths = []; // 每一列的宽度
        let middleLeafColumns = [];
        let useTotal = false;
        let formatColumnMap = {};
        let { columns, columnsWidth, addBlankCol = true } = this.props,
            { bodyWidth } = this.state;

        let ncc_automated = getGlobalStorage('localStorage', '_ncc_automated_');

        columnsWidth = { ...columnsWidth };

        let fixedDataIndex = 0, colSpanCnt = 0;
        let { height } = formatColumn(columns, column => {
            column = { ...column };
            let { isleaf = true, fixed, depth = 0, width = 0, key, visible = true, istotal, dataIndex, colSpan = 1 } = column;

            // 合并单元格中第一个单元格的index
            let colSpanDataIndex = dataIndex;

            if (colSpan > 1) {
                fixedDataIndex = column.dataIndex;
                colSpanCnt = colSpan - 1;
            } else if (colSpanCnt > 0) {
                colSpanDataIndex = fixedDataIndex;
                colSpanCnt--;
            }

            column = { ...column, colSpanDataIndex };

            if (isNaN(width)) {
                width = 0;
            }

            if (ncc_automated == '1') {
                // 自动化测试开关打开
                fixed = false;
            }

            if (isleaf) {
                if (istotal) {
                    useTotal = true;
                }

                if (!visible) {
                    width = 0;
                } else if (columnsWidth[key]) {
                    width = columnsWidth[key];
                } else {
                    columnsWidth[key] = width;
                }

                column = { ...column, width };
            }

            // 多表头不支持固定列
            switch (isleaf && depth === 0 && fixed) {
                case 'left':
                case true:
                    column._fixed = 'left';
                    leftColumns.push(column);
                    leftWidth += column.width;
                    leftWidths.push(column.width);
                    break;
                case 'right':
                    column._fixed = 'right';
                    rightColumns.push(column);
                    rightWidth += column.width;
                    rightWidths.push(column.width);
                    break;
                default:
                    column._fixed = 'middle';
                    middleColumns.push(column);
                    if (isleaf) {
                        middleLeafColumns.push(column);
                        middleWidth += column.width;
                        middleWidths.push(column.width);
                    }
                    break;
            }

            formatColumnMap[column.key] = column;

            return column;
        });

        // 添加空白的占位列，用于优化显示
        let blankWidth = bodyWidth - leftWidth - middleWidth - rightWidth - (this.hasVerticalScrollBar ? scrollBarSize : 0);
        if (addBlankCol && blankWidth > 20) {
            let middleLastRange = middleColumns[middleColumns.length - 1]?.range ?? [0, 1];
            let blankCol = {
                title: null,
                key: 'BLANK_COL',
                width: blankWidth,
                isleaf: true,
                depth: 0,
                path: ['BLANK_COL'],
                range: [middleLastRange[1], middleLastRange[1] + 1],
                render: () => null,
            };

            let lastMiddleColumn = middleColumns[middleColumns.length - 1],
                lastMiddleLeafColumn = middleLeafColumns[middleLeafColumns.length - 1];

            if (lastMiddleLeafColumn) {
                blankCol.leafIndex = lastMiddleLeafColumn.leafIndex + 1;
            } else {
                blankCol.leafIndex = 0;
            }

            if (lastMiddleColumn) {
                blankCol.dataIndex = lastMiddleLeafColumn.dataIndex + 1;
            } else {
                blankCol.dataIndex = 1;
            }
            blankCol.colSpanDataIndex = blankCol.dataIndex;

            rightColumns.forEach(col => {
                col.leafIndex++;
                col.dataIndex++;
                col.range = col.range.map(e => e + 1);
                col.viewIndex++;
                col.colSpanDataIndex = col.dataIndex;
            });

            middleColumns.push(blankCol);
            middleLeafColumns.push(blankCol);
            middleWidths.push(blankCol.width);
            middleWidth += blankCol.width;
            columnsWidth[blankCol.key] = blankCol.width;
        }

        let leafColumns = [].concat(leftColumns, middleLeafColumns, rightColumns),
            leafWidth = [].concat(leftWidths, middleWidths, rightWidths),
            flatColumns = [].concat(leftColumns, middleColumns, rightColumns);

        // TODO:上下一共遍历了两次，看是否优化
        return {
            headerRowsCount: height,
            flatColumns, // TODO:header算法
            formatColumnMap,
            rawColumns: columns,
            left: {
                columns: leftColumns,
                leafColumns: leftColumns,
                leftColumnsMap: leftColumns.reduce((map, col) => {
                    map[col.key] = col;
                    return map;
                }, {}),
                ...this.calcWidthInfo(leftColumns),
            },
            middle: {
                columns: middleColumns,
                columnsWidth: middleWidths,
                leafColumns: middleLeafColumns,
                ...this.calcWidthInfo(middleColumns),
            },
            right: {
                columns: rightColumns,
                leafColumns: rightColumns,
                rightColumnsMap: rightColumns.reduce((map, col) => {
                    map[col.key] = col;
                    return map;
                }, {}),
                ...this.calcWidthInfo(rightColumns, { reverse: true }),
            },
            leaf: {
                columns: leafColumns,
                columnsWidth: leafWidth,
                columnsWidthMap: columnsWidth,
            },
            useTotal,
        };
    }

    // 计算总宽度以及偏移量
    calcWidthInfo = (columns, { reverse = false } = {}) => {
        return columns[reverse ? 'reduceRight' : 'reduce'](({ offsets, width }, { width: columnWidth, isleaf }) => {
            if (isleaf) {
                offsets[reverse ? 'unshift' : 'push'](width);
                width += columnWidth;
            }
            return { offsets, width };
        }, { offsets: [], width: 0 });
    }

    bindEvent = () => {
        // 接管默认的滚动事件
        // 解决纵向滚动有些许白屏的问题
        // 解决横向滚动有些许错位的问题
        this.horizontalScrollElements.forEach(element => {
            element.current.addEventListener('wheel', this.syncHorizontalWheel);
            element.current.addEventListener('scroll', this.handleHorizontalScroll);
        });
        this.verticalScrollElements.forEach(element => {
            element.current.addEventListener('wheel', this.syncVerticalWheel);
            element.current.addEventListener('scroll', this.handleVerticalScroll);
        });

        let { scrollContainer } = this.props;

        scrollContainer?.()?.addEventListener('scroll', this.handleWindowScroll);
        window.addEventListener('resize', this.handleWindowScroll);
    }

    removeEvent = () => {
        this.horizontalScrollElements.forEach(element => {
            element.current.removeEventListener('wheel', this.syncHorizontalWheel);
            element.current.removeEventListener('scroll', this.handleHorizontalScroll);
        });
        this.verticalScrollElements.forEach(element => {
            element.current.removeEventListener('wheel', this.syncVerticalWheel);
        });

        let { scrollContainer } = this.props;

        scrollContainer?.()?.removeEventListener('scroll', this.handleWindowScroll);
        window.removeEventListener('resize', this.handleWindowScroll);
    }

    updateDistance = throttle((containerBottom, fixedBottom) => {
        fixedBottom && this.setState({ fixedDistance: this.getDOMBottom(this.getFixedContainer()) - containerBottom });
    }, 50);

    // TODO:后面优化一下可读性
    handleWindowScroll = event => {
        let { fixHeader, fixFooter, scrollContainer: getScrollContainer } = this.props;

        let { fixedTop, fixedBottom } = this.state;

        let scrollContainer = getScrollContainer?.(),
            containerBottom = this.getDOMBottom(scrollContainer);

        fixFooter && [
            this.horizontalScrollBarRef,
        ].forEach(e => {
            let { bottom } = e.current.getBoundingClientRect();
            if (
                // 表格底部高于容器底部
                (containerBottom >= (this.tableBodyWrapperRef.current.getBoundingClientRect().bottom + this.horizontalScrollBarRef.current.offsetHeight + this.tableFooterWrapperRef.current.offsetHeight)) ||
                // 表格头部低于容器底部
                ((this.tableHeaderWrapperRef.current.getBoundingClientRect().bottom + this.horizontalScrollBarRef.current.offsetHeight + this.tableFooterWrapperRef.current.offsetHeight) > containerBottom)
            ) {
                if (fixedBottom) {
                    this.setState({
                        fixedBottom: false,
                        fixedDistance: 'auto',
                    });
                }
            } else if (bottom > containerBottom) { //滚动条托底
                if (!fixedBottom) {
                    this.setState({
                        fixedBottom: true,
                        fixedDistance: this.getDOMBottom(this.getFixedContainer()) - containerBottom,
                    });
                }
            } else if (!!event && bottom !== containerBottom) { // update和mount导致的触发不能setState
                this.updateDistance(containerBottom, fixedBottom);
            }
        });

        fixHeader && [
            this.tableHeaderWrapperRef,
        ].forEach(e => {
            let { top } = e.current.getBoundingClientRect();
            if (
                this.tableBodyWrapperRef.current.getBoundingClientRect().top >
                this.tableHeaderWrapperRef.current.offsetHeight ||
                this.horizontalScrollBarRef.current.getBoundingClientRect().bottom < 0
            ) {
                if (fixedTop) {
                    this.setState({ fixedTop: false });
                }
            } else if (top < 0) {
                if (!fixedTop) {
                    this.setState({ fixedTop: true });
                }
            }
        });
    }

    getDOMBottom = DOMNode => {
        let fixedContainerBottom;

        if (DOMNode === window || DOMNode === document || DOMNode === document.body || !DOMNode) {
            fixedContainerBottom = document.body.clientHeight;
        } else {
            fixedContainerBottom = DOMNode.getBoundingClientRect().bottom;
        }

        return fixedContainerBottom;
    }

    getFixedContainer = () => {
        let TableDOM = findDOMNode(this),
            parentNode = TableDOM.parentElement;
        while (parentNode.tagName !== 'BODY') { // 设计器里document.body好像是设计器的document，后面再看下
            let { transform, perspective, filter } = getComputedStyle(parentNode);

            // 这三个属性会影响position: fixed的定位基准
            if (transform !== 'none' || perspective !== 'none' || filter !== 'none') {
                break;
            }

            parentNode = parentNode.parentElement;
        }

        return parentNode;
    }

    handleScroll = () => {
        let { scrollLeft, scrollTop } = this.state;

        this.props.onScroll?.({
            scrollLeft,
            scrollTop,
        });
    }

    // 横向滚动事件   设置除自己之外的3个滚动元素(表头、表体、合计、滚动条)
    // 假设表头滑动， 首先设置四个滚动元素，然后把自身删掉，剩余3个元素去滚动。 剩余3个元素也会触发，但都会被this.readyToScrollX.has(currentTarget)return掉
    handleHorizontalScroll = event => {
        let { currentTarget } = event,
            { scrollLeft } = this.state;
        let currentScrollLeft = currentTarget.scrollLeft;
        // let scrollFlag = false; 

        // if(currentTarget.style.height =="") { //内部页签之间切换，滚动条没保留 YBP-FI-BUG-2025
        //     currentScrollLeft = scrollLeft; 
        //     scrollFlag = true;  
        // }
        // console.log(scrollLeft, currentTarget, currentScrollLeft);
        // 加个判断条件，判断是首次滚动，首次滚动时!this.readyToScrollX.has(currentTarget) === true
        // 由scrollX触发的滚动，需要delete，而由于此时state.scrollLeft已经改变，会判等直接return，此时会有问题——NCC-298650
        if (!this.readyToScrollX.has(currentTarget) && scrollLeft === currentTarget.scrollLeft) { return; } //判断下方向

        // 横向滚动
        if (this.readyToScrollX.has(currentTarget)) {
            this.readyToScrollX.delete(currentTarget);
            return;
        } else {
            this.readyToScrollX = new Set(this.horizontalScrollElements.map(e => e.current));
            this.readyToScrollX.delete(currentTarget);
        }

        // 不通过wheel触发滚动
        // 比如：代码控制、focus等
        if (currentScrollLeft !== this.state.scrollLeft) {
            this.scrollX(currentScrollLeft, currentTarget);
            this.setState({ scrollLeft: currentScrollLeft }, () => {
                this.handleScroll();
            });
        }
    }

    // 纵向滚动事件
    handleVerticalScroll = event => {
        let { currentTarget } = event,
            currentScrollTop = currentTarget.scrollTop;

        // 纵向滚动
        if (this.readyToScrollY.has(currentTarget)) {
            this.readyToScrollY.delete(currentTarget);
            return;
        } else {
            this.readyToScrollY = new Set(this.verticalScrollElements.map(e => e.current));
            this.readyToScrollY.delete(currentTarget);
        }

        // 不通过wheel触发滚动
        // 比如：代码控制、focus等
        if (currentScrollTop !== this.state.scrollTop) {
            this.scrollY(currentScrollTop, currentTarget);
            this.setState({ scrollTop: currentScrollTop }, () => {
                this.handleScroll();
            });
        }
    }

    // 同步横向滚动（滚轮事件） 
    // 滚轮事件和横向滚动的区别是 滚轮需要自己设scrollLeft，横向滚动是用鼠标点击,设置其他3个即可
    syncHorizontalWheel = event => {
        let { currentTarget, deltaX, deltaY } = event;

        if (Math.abs(deltaX) >= Math.abs(deltaY) || event.shiftKey) {
            let { scrollLeft: currentScrollLeft, clientWidth, scrollWidth } = currentTarget,
                scrollLeft = currentScrollLeft + (Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY);

            if (scrollLeft > 0 && scrollLeft < scrollWidth - clientWidth) {
                // 禁用滚动
                // 解决overflow: hidden导致的外层抖动（触发了mac触摸板的前进后退）
            }
            event.preventDefault();

            currentTarget.scrollLeft = scrollLeft;
        }
    }

    // 同步纵向滚动（滚轮事件） 
    syncVerticalWheel = event => {
        let { currentTarget, deltaX, deltaY } = event;
        if (event.shiftKey && currentTarget === this.tableBodyWrapperRef.current) { return; } // tableBody区域+shift控制横向滚动

        if (Math.abs(deltaX) < Math.abs(deltaY)) {
            let { scrollTop: currentScrollTop, clientHeight, scrollHeight } = currentTarget,
                scrollTop = currentScrollTop + deltaY;

            if (scrollTop > 0 && scrollTop < scrollHeight - clientHeight) {
                // 禁用滚动
                // 解决overflow: hidden导致的外层抖动（触发了mac触摸板的前进后退）
            }

            if (!this.isScrollPreventDelay) {
                if (currentScrollTop === 0 || currentScrollTop === scrollHeight - clientHeight) { //滚动过临界值
                    this.isScrollPreventDelay = true;
                    this.hasVerticalScrollBar && event.preventDefault();
                    setTimeout(() => this.isScrollPreventDelay = false, 1000);
                }
            }
            //判断滚动条是否滑动到底部的时候，scrollHeight，clientHeight会取整，因此会有不到1px的误差，需要+1
            if (currentScrollTop > 0 && currentScrollTop + 1 < scrollHeight - clientHeight) {
                this.hasVerticalScrollBar && event.preventDefault();
            }

            this.scrollY(scrollTop);

        }
    }

    scrollX = (scrollLeft, current) => {
        this.horizontalScrollElements.forEach(element => {
            if (current !== element.current) {
                element.current.scrollLeft = scrollLeft;
            }
        });
    }

    scrollY = scrollTop => {
        this.verticalScrollElements.forEach(element => element.current.scrollTop = scrollTop);
    }
}

export default TableNext;